import Config from '@config';
import {
  Organization,
  OrganizationBaseGridify,
  OrganizationInput,
  OrganizationReadModelGDN,
  ReferenceBook,
} from '@gasbi/shared/common/features/organizations/types';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { omit } from 'lodash-es';
import { useSelector } from 'react-redux';

import {
  CACHE_EXPIRATION_TIME,
  CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
} from '@/features/constants';
import { extendWithRoles } from '@/features/roles/utils';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  cachedItemByIdSelector,
  infiniteQueryOptionsFn,
} from '@/features/utils';
import normalizePayload from '@/forms/utils/normalizePayload';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const normalizeOrganizationPayloadBeforeMutation = (
  values: Organization,
) => {
  // @ts-ignore
  const payload: Partial<OrganizationInput> = omit(values, [
    'type',
    'classifier',
    'economicSector',
    'representative',
    'region',
  ]);
  payload.typeId = values.type.id;
  payload.classifierId = values.classifier?.id || null;
  payload.economicSectorId = values.economicSector?.id || null;

  // @ts-ignore
  delete payload.address;

  // @ts-ignore
  payload.settlementId = values?.settlement?.id;
  // @ts-ignore
  payload.streetName = values?.street?.name || payload.streetName;
  // @ts-ignore
  payload.regionId = values?.settlement?.regionId || values.region?.id;

  return normalizePayload(payload);
};
const normalizeOrganizationResponse = (data: Organization) => {
  if (data?.certification.code === null) {
    data.certification.code = '';
  }
  if (data?.certification.date === null) {
    data.certification.date = '';
  }
  if (data?.representative?.email === null) {
    data.representative.email = '';
  }
  if (data?.representative?.phoneNumber === null) {
    data.representative.phoneNumber = '';
  }
  if (data?.details.logo === null) {
    data.details.logo = '';
  }
  return data;
};

interface ReferenceBooksRes {
  organizationTypes: ReferenceBook[];
  organizationClassifiers: ReferenceBook[];
  economicSectors: ReferenceBook[];
  departments: ReferenceBook[];
  employeePositions: ReferenceBook[];
  streetTypes: ReferenceBook[];
}

const api = createApi({
  reducerPath: 'organizationsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/organizations`,
  }),
  tagTypes: [
    'Organizations',
    'Organization',
    'OrganizationClassifiers',
    'OrganizationDepartments',
    'OrganizationEmployeePositions',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => {
    return {
      getReferenceBooks: builder.query<ReferenceBooksRes, void>({
        keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
        query: () => ({
          url: '/reference-books',
        }),
      }),
      getOrganizationClassifiers: builder.query<ReferenceBook[], void>({
        query: () => ({
          url: `/reference-books`,
        }),
        providesTags: () => ['OrganizationClassifiers'],
        transformResponse: (response: ReferenceBooksRes) =>
          response?.organizationClassifiers,
      }),
      getOrganizationDepartments: builder.query<ReferenceBook[], void>({
        query: () => ({
          url: `/reference-books`,
        }),
        providesTags: () => ['OrganizationDepartments'],
        transformResponse: (response: ReferenceBooksRes) =>
          response?.departments,
      }),
      getOrganizationEmployeePositions: builder.query<ReferenceBook[], void>({
        query: () => ({
          url: `/reference-books`,
        }),
        providesTags: () => ['OrganizationEmployeePositions'],
        transformResponse: (response: ReferenceBooksRes) =>
          response?.employeePositions,
      }),
      getOrganizations: builder.infiniteQuery<
        OrganizationBaseGridify,
        FiltersGrid,
        Pagination
      >({
        infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 100 }),
        query: ({ queryArg, pageParam }) => ({
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        }),
        providesTags: () => ['Organizations'],
      }),
      getOrganizationsGdn: builder.query<OrganizationReadModelGDN[], void>({
        query: () => ({
          url: `/query/gdn`,
        }),
        providesTags: () => ['Organizations'],
      }),
      getOrganizationById: builder.query<Organization, string>({
        query: id => ({
          url: `/${id}`,
        }),
        transformResponse: normalizeOrganizationResponse,
        providesTags: result => [{ type: 'Organization', id: result?.id }],
      }),
      createOrganization: builder.mutation<Organization, Organization>({
        query: data => ({
          url: `/`,
          method: 'POST',
          data: normalizeOrganizationPayloadBeforeMutation(data),
        }),
        invalidatesTags: result => [
          'Organizations',
          { type: 'Organization', id: result?.id },
        ],
      }),
      updateOrganizationById: builder.mutation<Organization, Organization>({
        query: data => ({
          url: `/${data.id}`,
          method: 'PUT',
          data: normalizeOrganizationPayloadBeforeMutation(data),
        }),
        invalidatesTags: result => [
          'Organizations',
          { type: 'Organization', id: result?.id },
        ],
      }),
      deleteOrganizationById: builder.mutation<Organization, string>({
        query: id => ({
          url: `/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, __, id) => [
          'Organizations',
          { type: 'Organization', id },
        ],
      }),
      addRepresentativeToOrganization: builder.mutation<
        Organization,
        { organizationId: string; representativeId: string }
      >({
        query: ({ organizationId, representativeId }) => ({
          url: `/${organizationId}/representative/assign`,
          method: 'POST',
          data: { representativeId },
        }),
        invalidatesTags: (_, __, { organizationId }) => [
          'Organizations',
          { type: 'Organization', organizationId },
        ],
      }),
      removeRepresentativeFromOrganization: builder.mutation<
        Organization,
        { organizationId: string; representativeId: string }
      >({
        query: ({ organizationId, representativeId }) => ({
          url: `/${organizationId}/representative/unassign`,
          method: 'POST',
          data: { representativeId },
        }),
        invalidatesTags: (_, __, { organizationId }) => [
          'Organizations',
          { type: 'Organization', organizationId },
        ],
      }),
    };
  },
});

export const organizationsApi = extendWithRoles(api as any);

export const {
  useGetReferenceBooksQuery,
  useGetOrganizationsGdnQuery,
  useGetOrganizationByIdQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationByIdMutation,
  useGetOrganizationClassifiersQuery,
  useGetOrganizationDepartmentsQuery,
  useGetOrganizationEmployeePositionsQuery,
  useAddRepresentativeToOrganizationMutation,
  useRemoveRepresentativeFromOrganizationMutation,
} = api;
export const useGetOrganizationsInfiniteQuery = normalizeInfiniteQueryData(
  api.useGetOrganizationsInfiniteQuery,
);

export const useGetOrganizationRolesInfiniteQuery = normalizeInfiniteQueryData(
  organizationsApi.useGetOrganizationRolesInfiniteQuery,
);

const getCachedOrganizationByIdSelector = cachedItemByIdSelector(
  'organizationsApi',
  'getOrganizations',
);

export const useGetCachedOrganizationByIdSelector = id =>
  useSelector(getCachedOrganizationByIdSelector(id));

const getCachedRoleByIdSelector = cachedItemByIdSelector(
  'organizationsApi',
  'getOrganizationRoles',
);

export const useGetCachedRoleByIdSelector = id =>
  useSelector(getCachedRoleByIdSelector(id));
